import { Mail } from "@mui/icons-material";
import {
  Box,
  Button,
  ButtonGroup,
  Grid,
  IconButton,
  Paper,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { pdf, PDFDownloadLink } from "@react-pdf/renderer";
import CircularLoader from "components/CircularLoader";
import GenericModal from "components/GenericModal";
import exportInvoiceSheet from "layouts/pages/payroll/actions/exportInvoiceSheet";
import { useMemo, useState } from "react";
import { useMutation } from "react-query";
import InvoiceEditorPreviewPDF from "../InvoiceEditorPreviewPDF";

const InvoiceContainer = styled(Box)(({ theme }) => ({
  //   position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  //   backgroundColor: "rgba(0, 0, 0, 0.5)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1300,
  maxHeight: "70vh",
  //   overflowY: "auto",
}));

const InvoiceContent = styled(Paper)(({ theme }) => ({
  backgroundColor: "white",
  border: `1px solid ${theme.palette.grey[200]}`,
  borderRadius: theme.shape.borderRadius,
  //   boxShadow: theme.shadows[24],
  maxWidth: "1024px",
  width: "100%",
  margin: theme.spacing(2),
  display: "flex",
  flexDirection: "column",
  maxHeight: "70vh",
  //   overflowY: "auto",
}));

const InvoiceHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.grey[200]}`,
}));

const ViewModeToggle = styled(ButtonGroup)(({ theme }) => ({
  backgroundColor: theme.palette.grey[100],
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(0.5),
}));

const InvoiceBody = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  overflowY: "auto",
  flexShrink: 1,
}));

const CompanyInfo = styled(Box)(({ theme }) => ({
  textAlign: "right",
}));

const TotalsContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  paddingTop: theme.spacing(2),
  borderTop: `1px solid ${theme.palette.grey[200]}`,
}));

const TotalsBox = styled(Box)(({ theme }) => ({
  width: "256px",
  "& > div": {
    marginBottom: theme.spacing(1),
  },
}));

const NotesBox = styled(Box)(({ theme }) => ({
  width: "512px",
  "& > div": {
    marginBottom: theme.spacing(1),
  },
  whiteSpace: "pre-line",
}));

const ActionBar = styled(Box)(({ theme }) => ({
  flexBasis: "auto",
  flexGrow: 0,
  display: "flex",
  justifyContent: "flex-end",
  gap: theme.spacing(1),
  padding: theme.spacing(2),
  borderTop: `1px solid ${theme.palette.grey[200]}`,
  backgroundColor: theme.palette.grey[50],
}));

const TableCellSmaller = styled(TableCell)(({ theme }) => ({
  "& p": {
    fontSize: "0.9rem",
  },
}));
const TableHeaderCellSmaller = styled(TableCell)(({ theme }) => ({
  fontSize: "0.9rem",
  fontWeight: "medium",
  color: "text.secondary",
}));

const moneyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

const InvoiceEditorPreviewModal = ({
  open,
  setOpen,
  detailRows,
  summaryRows,
  invoiceNumber = "0",
  setInvoiceFile,
  from = {},
  to = {},
  invoiceInformation = {},
  isShiftJob,
  startDate,
  burdenRate,
  currentEvent,
  currentJob,
  isLoading,
  isVoid,
  invoiceId,
}) => {
  const [invoiceViewMode, setInvoiceViewMode] = useState("summary");

  const [subTotal, tax, discount, total] = useMemo(() => {
    let localSubTotal = 0;
    let localTotal = 0;
    // eslint-disable-next-line prefer-const
    let localTax = 0;
    // eslint-disable-next-line prefer-const
    let localDiscount = 0;

    // Burden Rate
    const burdenInfo = burdenRate?.data?.burdenInfo;
    const calculatedBurdenRate =
      (burdenInfo?.payrollTaxes ?? 0) +
      (burdenInfo?.futa ?? 0) +
      (burdenInfo?.suta ?? 0) +
      (burdenInfo?.workersCompensation ?? 0) +
      (burdenInfo?.benefits ?? 0) +
      (burdenInfo?.administrativeCosts ?? 0);

    if (invoiceViewMode === "details") {
      detailRows?.forEach((row) => {
        localSubTotal +=
          (row?.totalHours || 0) * (row?.billRate || 0) +
          (row?.totalOvertimeHours || 0) * (row?.billRate || 0) * 1.5;
      });
    } else {
      summaryRows?.forEach((row) => {
        localSubTotal +=
          (row?.totalHours || 0) * (row?.billRate || 0) +
          (row?.totalOvertimeHours || 0) * (row?.billRate || 0) * 1.5;
      });
    }

    // localTax = localSubTotal * calculatedBurdenRate;
    localTotal = localSubTotal + localTax;

    return [localSubTotal, localTax, localDiscount, localTotal];
  }, [burdenRate?.data?.burdenInfo, detailRows, invoiceViewMode, summaryRows]);

  const getTotalAmount = (row) => {
    const tot =
      (row?.totalHours || 0) * (row?.billRate || 0) +
      (row?.totalOvertimeHours || 0) * (row?.billRate || 0) * 1.5;

    return `${moneyFormatter.format(tot)}`;
  };

  const handleExportSheet = async () => {
    const response = await exportInvoiceSheet({ invoiceId });

    // Extract filename from Content-Disposition header
    const contentDisposition = response.headers["content-disposition"];
    let filename = "download.xlsx"; // fallback filename

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/);
      if (filenameMatch) {
        // eslint-disable-next-line prefer-destructuring
        filename = filenameMatch[1];
      }
    }

    const url = window.URL.createObjectURL(response.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const invoiceFilename = `${invoiceNumber?.toString().padStart(8, "0")}-${(isShiftJob
    ? currentJob?.title ?? ""
    : currentEvent?.eventName ?? ""
  ).replace(/\W/g, "_")}-${invoiceViewMode}-${startDate}`;

  const handleDownloadPDF = async () => {
    const blob = await pdf(
      <InvoiceEditorPreviewPDF
        invoiceNumber={invoiceNumber}
        invoiceInformation={invoiceInformation}
        from={from}
        to={to}
        invoiceViewMode={invoiceViewMode}
        detailRows={detailRows}
        summaryRows={summaryRows}
        subTotal={subTotal}
        tax={tax}
        total={total}
        discount={discount}
        getTotalAmount={getTotalAmount}
        isShiftJob={isShiftJob}
        isVoid={isVoid}
      />
    ).toBlob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${invoiceFilename}.pdf`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const invoiceBody = (
    <InvoiceBody>
      {/* Invoice Header */}
      <Grid container spacing={4} sx={{ mb: 4 }}>
        <Grid item xs={6}>
          <Typography
            variant="h4"
            component="h2"
            sx={{ fontWeight: "bold", mb: 2, display: "inline-block" }}
          >
            INVOICE
          </Typography>
          {isVoid && (
            <Typography
              variant="h4"
              color="error"
              component="h2"
              sx={{ fontWeight: "bold", mb: 2, ml: 1, display: "inline-block" }}
            >
              (VOID)
            </Typography>
          )}
          <Box sx={{ "& > div": { mb: 0.5 } }}>
            <Typography variant="body2">
              <strong>Invoice Number:</strong> {invoiceNumber}
            </Typography>
            <Typography variant="body2">
              <strong>Purchase Order:</strong> {invoiceInformation.purchaseOrder || "-"}
            </Typography>
            <Typography variant="body2">
              <strong>Invoice Date:</strong> {invoiceInformation.date}
            </Typography>
            <Typography variant="body2">
              <strong>Due Date:</strong> {invoiceInformation.dueDate}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6} sx={{ marginTop: 2 }}>
          <CompanyInfo>
            <Typography variant="body2" color="text.secondary">
              From:
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: "medium" }}>
              {from.name}
            </Typography>
            <Typography variant="body2">{from.address}</Typography>
            <Typography variant="body2">
              {from.city}, {from.state} {from.zip}
            </Typography>
            <Typography variant="body2">
              <strong>Attn:</strong> {from.attn || "-"}
            </Typography>
          </CompanyInfo>
        </Grid>
      </Grid>

      {/* Customer Information */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="body2" color="text.secondary">
          Bill To:
        </Typography>
        <Typography variant="body1" sx={{ fontWeight: "medium" }}>
          {to.name}
        </Typography>
        <Typography variant="body2">{to.address}</Typography>
        <Typography variant="body2">
          {to.city}, {to.state} {to.zip}
        </Typography>
        <Typography variant="body2">
          <strong>Attn:</strong> {to.attn || "-"}
        </Typography>
      </Box>

      {/* Invoice Items */}
      <Box sx={{ mb: 4 }}>
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead sx={{ display: "table-header-group" }}>
              <TableRow sx={{ backgroundColor: "grey.50" }}>
                {invoiceViewMode === "details" ? (
                  <>
                    {!isShiftJob && <TableHeaderCellSmaller>DATE</TableHeaderCellSmaller>}
                    <TableHeaderCellSmaller>{isShiftJob ? "JOB" : "EVENT"}</TableHeaderCellSmaller>
                    <TableHeaderCellSmaller>EMPLOYEE NAME</TableHeaderCellSmaller>
                    <TableHeaderCellSmaller>POSITION</TableHeaderCellSmaller>
                    <TableHeaderCellSmaller>PAYCODE</TableHeaderCellSmaller>
                    <TableHeaderCellSmaller>REG HOURS</TableHeaderCellSmaller>
                    <TableHeaderCellSmaller>OT HOURS</TableHeaderCellSmaller>
                    <TableHeaderCellSmaller>RATE</TableHeaderCellSmaller>
                    <TableHeaderCellSmaller>AMOUNT</TableHeaderCellSmaller>
                  </>
                ) : (
                  <>
                    <TableHeaderCellSmaller>{isShiftJob ? "JOB" : "EVENT"}</TableHeaderCellSmaller>
                    {!isShiftJob && <TableHeaderCellSmaller>DATE</TableHeaderCellSmaller>}
                    <TableHeaderCellSmaller>POSITION</TableHeaderCellSmaller>
                    <TableHeaderCellSmaller>QTY STAFF</TableHeaderCellSmaller>
                    <TableHeaderCellSmaller>RATE</TableHeaderCellSmaller>
                    <TableHeaderCellSmaller>AMOUNT</TableHeaderCellSmaller>
                  </>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {invoiceViewMode === "details"
                ? detailRows.map((item, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <TableRow key={index}>
                      {!isShiftJob && (
                        <TableCellSmaller>
                          <Typography variant="body2">{item.date}</Typography>
                        </TableCellSmaller>
                      )}
                      <TableCellSmaller>
                        <Typography variant="body2">
                          {isShiftJob ? item.jobName : item.eventName}
                        </Typography>
                      </TableCellSmaller>
                      <TableCellSmaller>
                        <Typography variant="body2" sx={{ fontWeight: "medium" }}>
                          {item.firstName} {item.lastName}
                        </Typography>
                      </TableCellSmaller>
                      <TableCellSmaller>
                        <Typography variant="body2">
                          {item.positionName || (isShiftJob ? "Job Staff" : "Event Staff")}
                        </Typography>
                      </TableCellSmaller>
                      <TableCellSmaller>
                        <Typography variant="body2">{item.earningId}</Typography>
                      </TableCellSmaller>
                      <TableCellSmaller>
                        <Typography variant="body2">{item.totalHours}</Typography>
                      </TableCellSmaller>
                      <TableCellSmaller>
                        <Typography variant="body2">{item.totalOvertimeHours || "0"}</Typography>
                      </TableCellSmaller>
                      <TableCellSmaller>
                        <Typography variant="body2">
                          {moneyFormatter.format(parseFloat(item.billRate?.toString() || "0"))}
                        </Typography>
                      </TableCellSmaller>
                      <TableCellSmaller>
                        <Typography variant="body2">{getTotalAmount(item)}</Typography>
                      </TableCellSmaller>
                    </TableRow>
                  ))
                : summaryRows.map((item, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <TableRow key={index}>
                      <TableCellSmaller>
                        <Typography variant="body2">
                          {isShiftJob ? item.jobName : item.eventName}
                        </Typography>
                      </TableCellSmaller>
                      {!isShiftJob && (
                        <TableCellSmaller>
                          <Typography variant="body2">{item.date}</Typography>
                        </TableCellSmaller>
                      )}
                      <TableCellSmaller>
                        <Typography variant="body2">
                          {item.positionName || (isShiftJob ? "Job Staff" : "Event Staff")}
                        </Typography>
                      </TableCellSmaller>
                      <TableCellSmaller>
                        <Typography variant="body2">{item.totalEmployees}</Typography>
                      </TableCellSmaller>
                      <TableCellSmaller>
                        <Typography variant="body2">
                          {moneyFormatter.format(parseFloat(item.billRate?.toString() || "0"))}
                        </Typography>
                      </TableCellSmaller>
                      <TableCellSmaller>
                        <Typography variant="body2">{getTotalAmount(item)}</Typography>
                      </TableCellSmaller>
                    </TableRow>
                  ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Invoice Totals */}
      <TotalsContainer>
        <NotesBox>
          <Typography variant="body2">
            <strong>Notes:</strong> {from?.notes || "-"}
          </Typography>
        </NotesBox>
        <TotalsBox>
          {/* <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography variant="body1">Subtotal:</Typography>
            <Typography variant="body1">${subTotal?.toFixed(2)}</Typography>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography variant="body1">Discount:</Typography>
            <Typography variant="body1">${discount?.toFixed(2)}</Typography>
          </Box> */}
          {/* <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography variant="body1">Tax:</Typography>
            <Typography variant="body1">${tax.toFixed(2)}</Typography>
          </Box> */}
          {/* <Divider sx={{ my: 1 }} /> */}
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
              Total:
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
              {moneyFormatter.format(total)}
            </Typography>
          </Box>
        </TotalsBox>
      </TotalsContainer>
    </InvoiceBody>
  );

  const modalBody = (
    <InvoiceContainer>
      <InvoiceContent>
        {isLoading ? (
          <Grid height="200px">
            <CircularLoader hasBackdrop={false} isLoading={isLoading} color="info" />
          </Grid>
        ) : (
          invoiceBody
        )}
        <ActionBar>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => setOpen(false)}
            sx={{
              backgroundColor: "grey.500",
              "&:hover": { backgroundColor: "grey.600" },
            }}
          >
            Close
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleExportSheet}
            disabled={isLoading}
            sx={{
              backgroundColor: "blue.600",
              "&:hover": { backgroundColor: "blue.700" },
            }}
          >
            Download Spreadsheet
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleDownloadPDF}
            disabled={isLoading}
            sx={{
              backgroundColor: "blue.600",
              "&:hover": { backgroundColor: "blue.700" },
            }}
          >
            Download PDF
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Mail />}
            key={invoiceViewMode}
            onClick={async () => {
              const blob = await pdf(
                <InvoiceEditorPreviewPDF
                  invoiceNumber={invoiceNumber}
                  invoiceInformation={invoiceInformation}
                  from={from}
                  to={to}
                  invoiceViewMode={invoiceViewMode}
                  detailRows={detailRows}
                  summaryRows={summaryRows}
                  subTotal={subTotal}
                  tax={tax}
                  total={total}
                  discount={discount}
                  getTotalAmount={getTotalAmount}
                  isShiftJob={isShiftJob}
                  isVoid={isVoid}
                />
              ).toBlob();
              setInvoiceFile({
                file: blob,
                fileName: invoiceFilename,
                subType: startDate?.replace(/-/g, "_"),
              });
              // setOpenSendMessage(true);
            }}
            disabled={isLoading}
            sx={{
              backgroundColor: "blue.600",
              "&:hover": { backgroundColor: "blue.700" },
            }}
          >
            Send Email
          </Button>
        </ActionBar>
      </InvoiceContent>
    </InvoiceContainer>
  );

  const header = (
    <InvoiceHeader>
      <Typography variant="h6" component="h3" sx={{ fontWeight: "semibold" }}>
        Invoice Preview - {invoiceNumber}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <ViewModeToggle variant="contained" size="small">
          <Button
            onClick={() => setInvoiceViewMode("summary")}
            variant={invoiceViewMode === "summary" ? "contained" : "text"}
            size="small"
            sx={{
              backgroundColor: invoiceViewMode === "summary" ? "white" : "transparent",
              boxShadow: invoiceViewMode === "summary" ? 1 : 0,
              color: invoiceViewMode === "summary" ? "white" : "text.secondary",
            }}
          >
            Summary
          </Button>
          <Button
            onClick={() => setInvoiceViewMode("details")}
            variant={invoiceViewMode === "details" ? "contained" : "text"}
            size="small"
            sx={{
              backgroundColor: invoiceViewMode === "details" ? "white" : "transparent",
              boxShadow: invoiceViewMode === "details" ? 1 : 0,
              color: invoiceViewMode === "details" ? "white" : "text.secondary",
            }}
          >
            Details
          </Button>
        </ViewModeToggle>
        <IconButton
          onClick={() => setOpen(false)}
          sx={{
            color: "text.secondary",
            "&:hover": { color: "text.primary" },
          }}
        >
          ✕
        </IconButton>
      </Box>
    </InvoiceHeader>
  );

  return (
    <>
      <GenericModal
        open={open}
        setOpen={setOpen}
        body={modalBody}
        header={header}
        getTotalAmount={getTotalAmount}
        sx={(theme) => ({
          width: "90% !important",
          maxWidth: "1100px !important",
          overflow: "hidden !important",
          [theme.breakpoints.down("md")]: {
            "& .MuiCardContent-root": {
              padding: "0px",
            },
          },
        })}
      />
    </>
  );
};

// Setting default values for the props
InvoiceEditorPreviewModal.defaultProps = {};

// Typechecking props
InvoiceEditorPreviewModal.propTypes = {};

export default InvoiceEditorPreviewModal;
